import type { IPersonModel } from "../models/person/person.model";
import type { IMissedCallCachedData } from "../models/common/cached-data.model";

type CallerPerson = Pick<IPersonModel, "FirstName" | "Name" | "CompanyName" | "Reference">;

interface ICallerLines {
    nameLines: string[];
    company: string;
}

const ISO_TIMESTAMP = /^\d{4}-\d{2}-\d{2}T/;

// A company contact has neither first nor last name; its Reference is the company name.
// The company line is dropped when it only repeats the name, same rule as the list.
export function getPopupCallerLines(person: CallerPerson): ICallerLines {
    const firstName = (person.FirstName || '').trim();
    const lastName = (person.Name || '').trim();
    const reference = (person.Reference || '').trim();
    const company = (person.CompanyName || '').trim();
    const nameLines = firstName || lastName ? [firstName, lastName].filter(Boolean) : [reference].filter(Boolean);
    return { nameLines, company: company !== nameLines.join(' ') ? company : '' };
}

export function withCallerFields(item: IMissedCallCachedData, person: IPersonModel): IMissedCallCachedData {
    return {
        ...item,
        name: (person.Reference || person.CompanyName || '').trim(),
        personId: person.Id,
        companyId: person.ShadowCompanyId || null,
        reference: (person.Reference || '').trim(),
        firstName: (person.FirstName || '').trim(),
        lastName: (person.Name || '').trim(),
        companyName: (person.CompanyName || '').trim()
    };
}

// Entries cached before first/last name were stored only have `reference` / `name`.
export function getMissedCallNameLine(item: IMissedCallCachedData): string {
    return [item.firstName, item.lastName].filter(Boolean).join(' ') || item.reference || item.name || '';
}

export function getMissedCallCompanyLine(item: IMissedCallCachedData): string {
    return item.companyName && item.companyName !== getMissedCallNameLine(item) ? item.companyName : '';
}

// Entries cached before the ISO timestamp hold a locale date string; show those as stored.
export function formatMissedCallTime(time: string, formatDateTime: (value: string) => string): string {
    return ISO_TIMESTAMP.test(time) ? formatDateTime(time) : time;
}
